// src/modules/requerimiento/views/devolucion/RevisionSistemas.jsx
// Revisión en Sistemas de una devolución, con el mismo diseño que la de adición: resumen con
// contadores, equipos devueltos agrupados por vehículo, chips devueltos y, al final, la nota de
// devolución y los datos del requerimiento plegados.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Card, CircularProgress, LinearProgress, Stack, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import PrintIcon from "@mui/icons-material/Print";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";

import ENV from "../../../../config/env";
import { getRequerimiento, getNotaPdfDevolucion } from "../../services/reqSolicitudes";
import { getDevolucionAsignados } from "../../services/devolucion";
import CentroEquipoDetalleDialog from "../../components/CentroEquipoDetalleDialog";
import ResumenRevision, { COLORES_REVISION } from "../../components/ResumenRevision";
import DatosRequerimientoPlegables from "../../components/DatosRequerimientoPlegables";
import { FilaModemDevuelto, ChipDevueltoCard, ModemDevueltoDialog } from "../../components/devolucion/DevueltosComponents";

const SIN_PLACA = "Sin placa";
const tieneFoto = (m) => !!String(m?.imagen_url ?? "").trim();

export default function DevolucionRevisionSistemas({ cod, onToast }) {
  const [req,          setReq]          = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [err,          setErr]          = useState(null);
  const [printing,     setPrinting]     = useState(false);
  const [asignados,    setAsignados]    = useState({ modems: [], chips: [] });
  const [loadingAsign, setLoadingAsign] = useState(false);
  const [errAsign,     setErrAsign]     = useState(null);
  const [modemSel,     setModemSel]     = useState(null);
  const [centroEquipo, setCentroEquipo] = useState(null);

  const toAbsUrl = useCallback((u) => {
    if (!u) return "";
    if (/^https?:\/\//i.test(u)) return u;
    return ENV.API_BASE_URL ? `${ENV.API_BASE_URL}${u}` : u;
  }, []);

  const fetchReq = useCallback(async () => {
    if (!cod) return;
    setLoading(true);
    setErr(null);
    try {
      const { data } = await getRequerimiento(cod);
      setReq(data);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Error al cargar requerimiento");
    } finally {
      setLoading(false);
    }
  }, [cod]);

  const fetchAsignados = useCallback(async () => {
    if (!cod) return;
    setLoadingAsign(true);
    setErrAsign(null);
    try {
      const { data } = await getDevolucionAsignados(cod);
      setAsignados({
        modems: Array.isArray(data?.["Modems devueltos"]) ? data["Modems devueltos"] : [],
        chips:  Array.isArray(data?.["Chips devueltos"])  ? data["Chips devueltos"]  : [],
      });
    } catch (e) {
      setErrAsign(e?.response?.data?.message || e?.message || "Error al cargar los devueltos");
    } finally {
      setLoadingAsign(false);
    }
  }, [cod]);

  useEffect(() => { fetchReq(); fetchAsignados(); }, [fetchReq, fetchAsignados]);

  const handleImprimir = async () => {
    try {
      setPrinting(true);
      const { data } = await getNotaPdfDevolucion(cod);
      const blob = new Blob([data], { type: "application/pdf" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.target = "_blank"; a.download = `nota-devolucion-${cod}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      onToast?.({ sev: "error", msg: e?.response?.data?.error || e?.message || "No se pudo generar el PDF" });
    } finally {
      setPrinting(false);
    }
  };

  // La foto se guarda con el endpoint de imagen del módem (historial del requerimiento): ya no se
  // reenvía la devolución completa, que la volvía a procesar como física.
  const handleImagenCambiada = useCallback(async () => {
    await fetchAsignados();
  }, [fetchAsignados]);

  // Al recargar, el detalle abierto se actualiza con la foto nueva.
  useEffect(() => {
    if (!modemSel) return;
    const clave = modemSel.num_sim || modemSel.id;
    const actual = asignados.modems.find((m) => (m.num_sim || m.id) === clave);
    if (actual && actual !== modemSel) setModemSel(actual);
  }, [asignados.modems, modemSel]);

  // Árbol por vehículo: la placa es el título y de ella cuelgan sus equipos devueltos.
  const grupos = useMemo(() => {
    const mapa = new Map();
    asignados.modems.forEach((m) => {
      const placa = String(m?.placa ?? "").trim() || SIN_PLACA;
      if (!mapa.has(placa)) mapa.set(placa, []);
      mapa.get(placa).push(m);
    });
    return Array.from(mapa.entries());
  }, [asignados.modems]);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress size={28} /></Box>;
  if (err)     return <Alert severity="error" action={<Button size="small" onClick={fetchReq}>Reintentar</Button>}>{err}</Alert>;

  const total = asignados.modems.length;
  const conFoto = asignados.modems.filter(tieneFoto).length;
  const todoBien = total > 0 && conFoto === total;
  const mensaje = loadingAsign
    ? "Cargando equipos devueltos…"
    : total === 0
      ? "No hay equipos devueltos en este requerimiento."
      : todoBien
        ? "Todo en orden. Ya se puede finalizar."
        : `${total - conFoto} equipo(s) sin foto de la devolución.`;

  return (
    <Stack spacing={2}>
      <ResumenRevision
        colores={COLORES_REVISION.devolucion}
        descripcion="Revisa que cada equipo devuelto tenga su foto y observación antes de finalizar."
        contadores={total > 0 && !loadingAsign ? [
          { etiqueta: "Equipos devueltos", valor: total },
          { etiqueta: "Con foto", valor: conFoto, total },
          ...(asignados.chips.length ? [{ etiqueta: "Chips", valor: asignados.chips.length }] : []),
        ] : []}
        mensaje={mensaje}
        todoBien={todoBien}
      />

      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Vehículos</Typography>

      {loadingAsign ? (
        <LinearProgress />
      ) : errAsign ? (
        <Alert severity="error" action={<Button size="small" onClick={fetchAsignados}>Reintentar</Button>}>{errAsign}</Alert>
      ) : total === 0 ? (
        <Typography variant="body2" color="text.secondary">No hay módems devueltos en este requerimiento.</Typography>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5, alignItems: "start" }}>
          {grupos.map(([placa, modems]) => (
            <Card key={placa} variant="outlined" sx={{ borderRadius: "10px" }}>
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 1.5, py: 1.25, bgcolor: "action.hover" }}>
                <DirectionsCarIcon fontSize="small" sx={{ color: "text.secondary" }} />
                <Typography sx={{ fontWeight: 800, flex: 1 }} noWrap>{placa}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {modems.length === 1 ? "1 equipo" : `${modems.length} equipos`}
                </Typography>
              </Stack>
              <Stack divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}>
                {modems.map((m) => (
                  <FilaModemDevuelto key={m.num_sim || m.id} modem={m} onClick={() => setModemSel(m)} />
                ))}
              </Stack>
            </Card>
          ))}
        </Box>
      )}

      {!loadingAsign && !errAsign && asignados.chips.length > 0 && (
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Chips devueltos ({asignados.chips.length})
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
            {asignados.chips.map((c) => (
              <ChipDevueltoCard
                key={c.numero}
                chip={c}
                onClick={c.numero ? () => setCentroEquipo({ tipo: "chip", identificador: String(c.numero) }) : undefined}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Nota en PDF y datos del requerimiento: de consulta, al final y compactos. */}
      <LoadingButton
        loading={printing}
        loadingPosition="start"
        startIcon={<PrintIcon />}
        variant="outlined"
        onClick={handleImprimir}
        sx={{ textTransform: "none", py: 1 }}
      >
        {printing ? "Generando…" : "Ver nota de devolución (PDF)"}
      </LoadingButton>

      {req && <DatosRequerimientoPlegables req={req} />}

      <ModemDevueltoDialog
        modem={modemSel}
        cod={cod}
        toAbsUrl={toAbsUrl}
        onClose={() => setModemSel(null)}
        onChanged={handleImagenCambiada}
        onToast={onToast}
        onVerEquipo={(id) => setCentroEquipo({ tipo: "modem", identificador: String(id) })}
      />

      <CentroEquipoDetalleDialog
        open={!!centroEquipo}
        tipo={centroEquipo?.tipo || "modem"}
        identificador={centroEquipo?.identificador}
        onClose={() => setCentroEquipo(null)}
      />
    </Stack>
  );
}
